/**
 * OnboardingLockedOverlay — full-page lock screen for routes that
 * require `is_onboarded`.
 *
 * Per user spec (iter 96):
 *   - Black background (no peeking through to whatever was rendered
 *     behind it — that's the privacy reason for the rewrite).
 *   - Same blur + lock-icon + "Return to Onboarding" CTA aesthetic
 *     as the previous Resources lock so the visual language stays
 *     consistent across the app.
 *   - Bottom nav stays clickable. Each bottom-nav button stays
 *     visible AND clickable, but tapping any locked destination
 *     simply re-renders this same screen — i.e. it acts as a
 *     "frozen" navigation until onboarding completes.
 *
 * NOTE: This component is rendered AT the page level, not as a
 * higher-order route guard. That's intentional — wrapping a page
 * with this lets each page keep its `usePageHeader` + bottom-nav
 * context exactly as it was before, so the layout chrome doesn't
 * shift when the lock appears.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { Button } from './ui/button';

const OnboardingLockedOverlay = ({ pageName = 'this page' }) => {
  const navigate = useNavigate();
  return (
    <div
      // iter175bz — Don't cover the header. Mobile header is h-14
      // (56px / 3.5rem), desktop header is h-16 (64px / 4rem). The
      // responsive Tailwind classes `top-14 md:top-16` cover both at
      // the correct breakpoint. We then ADD the iOS safe-area inset
      // via an inline style override so the overlay starts at the
      // header's bottom edge regardless of notch height.
      // Bottom is anchored above the bottom nav (h-16 = 4rem) so the
      // nav stays clickable per spec.
      className="fixed inset-x-0 top-14 md:top-16 bg-black z-50"
      style={{
        marginTop: 'env(safe-area-inset-top, 0px)',
        bottom: 'calc(4rem + env(safe-area-inset-bottom, 0px))',
      }}
      data-testid="onboarding-locked-overlay"
    >
      {/* Full opaque black background — no peeking through to
       *  protected content. */}
      <div className="absolute inset-0 bg-black" />
      {/* Blur layer for the consistent "frosted" feel. */}
      <div className="absolute inset-0 backdrop-blur-2xl bg-black/80" />
      {/* Centered lock + message + CTA. */}
      <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
        <div className="w-16 h-16 bg-amber-100/10 rounded-full flex items-center justify-center mb-4 ring-1 ring-amber-300/40">
          <Lock className="w-8 h-8 text-amber-300" />
        </div>
        <h2 className="text-xl font-bold mb-2 text-white">Complete Onboarding</h2>
        <p className="text-gray-300 max-w-sm mb-5">
          You need to complete your onboarding process before accessing {pageName}.
        </p>
        <Button
          onClick={() => navigate('/')}
          className="bg-[#586D52] hover:bg-[#48553f] text-white"
          data-testid="onboarding-locked-return-btn"
        >
          Return to Onboarding
        </Button>
      </div>
    </div>
  );
};

export default OnboardingLockedOverlay;
